import { useState, useEffect } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import mainlogo from "@/assets/mylistiq-logo-new.png";
import CallToActionFooter from "@/components/Footer";
import axiosInstance from "@/config/axiosInstance";
import { endpoints } from "@/config/apiEndpoints";

export default function ReplyToSender() {
  const { campaignId, agentId } = useParams();
  const [searchParams] = useSearchParams();
  const recipientId = searchParams.get("rid") || "";
  const queryEmail = searchParams.get("email") || "";

  const [agentName, setAgentName] = useState("");
  const [agentEmail, setAgentEmail] = useState("");
  const [loading, setLoading] = useState(!!(campaignId && agentId));
  const [fetchErr, setFetchErr] = useState("");

  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState("");

  useEffect(() => {
    if (!campaignId || !agentId) { setLoading(false); return; }

    const fetchContext = async () => {
      try {
        setLoading(true);
        const queryParams = new URLSearchParams({
          campaign_id: campaignId,
          agent_id: agentId,
        });
        if (recipientId) queryParams.append("recipient_id", recipientId);
        if (queryEmail) queryParams.append("email", queryEmail);

        const res = await axiosInstance.get(
          `${endpoints.replyToSenderContext}?${queryParams.toString()}`
        );
        setAgentName(res.data.agent_name || "");
        setAgentEmail(res.data.agent_email || "");
        setForm(prev => ({
          ...prev,
          name: res.data.recipient_name || prev.name,
          email: res.data.recipient_email || queryEmail || prev.email,
          subject: res.data.campaign_subject || res.data.suggested_subject || prev.subject,
        }));
      } catch {
        setFetchErr("Could not load recipient details.");
      } finally {
        setLoading(false);
      }
    };

    fetchContext();
  }, [campaignId, agentId, recipientId, queryEmail]);

  const handleChange = (e) =>
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSend = async () => {
    if (!isFormValid) return;
    setSending(true);
    setSendError("");
    try {
      await axiosInstance.post(endpoints.replyToSender, {
        campaign_id: campaignId,
        agent_id: agentId,
        from_name: form.name,
        from_email: form.email,
        subject: buildFullSubject(form.subject),
        message: form.message,
      });
      setSubmitted(true);
    } catch (err) {
      if (err.code === "ERR_NETWORK") {
        setSendError("Can't reach the server. Please try again in a moment.");
      } else if (err.response?.status === 400) {
        setSendError(err.response.data?.error || "Some required information is missing.");
      } else {
        setSendError(err?.response?.data?.error || "Failed to send message. Please try again.");
      }
    } finally {
      setSending(false);
    }
  };

  const handleCancel = () => {
    setForm({ name: "", email: "", subject: "", message: "" });
    setSubmitted(false);
  };
  const buildFullSubject = (subject) => {
    const trimmed = subject.trim();
    return /^re:\s*/i.test(trimmed) ? trimmed : `RE: ${trimmed}`;
  };

  const isFormValid = campaignId && agentId && form.name && form.email && form.subject && form.message;

  return (
    <div className="min-h-screen bg-background flex flex-col">

      {/* ── Header ── */}
      <header className="sticky top-0 z-50 backdrop-blur-md bg-gradient-to-b from-blue-100/90 to-blue-50/90">
        <div className="mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <img src={mainlogo} alt="MyListIQ" className="h-10 sm:h-12 w-auto drop-shadow-sm" />
            </Link>

            <div
              className="flex-1 min-w-0 overflow-hidden"
              style={{ maskImage: "linear-gradient(to right, transparent 0, black 48px, black calc(100% - 48px), transparent 100%)" }}
            >
              <div className="flex w-max" style={{ animation: "marquee 20s linear infinite" }}>
                {[...Array(4)].map((_, i) => (
                  <span key={i} className="whitespace-nowrap text-xs text-red-500 px-12">
                    Try it free for 7 Days - no risk, all reward
                  </span>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-3 sm:gap-6">
              <div className="hidden md:flex items-center space-x-6">
                <Link to="/#features" className="text-slate-600 hover:text-blue-600 font-medium transition-colors text-sm">Features</Link>
                <Link to="/#pricing" className="text-slate-600 hover:text-blue-600 font-medium transition-colors text-sm">Pricing</Link>
                <Link to="/about" className="text-slate-600 hover:text-blue-600 font-medium transition-colors text-sm">About</Link>
              </div>
              <Link
                to={localStorage.getItem("access_token") ? "/dashboard" : "/signup"}
                className="text-sm font-semibold bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-md hover:shadow-xl transition-all px-3 sm:px-4 py-2 rounded-md"
              >
                {localStorage.getItem("access_token") ? "Dashboard" : "Sign Up"}
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* ── Main ── */}
      <main className="flex-1 flex items-center justify-center px-4 py-12 bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
        <div className="w-full max-w-lg">

          {/* <div className="flex items-center gap-1.5 text-xs text-slate-400 mb-4">
            <Link to="/" className="hover:text-blue-600 transition-colors">Home</Link>
            <span>/</span>
            <span className="text-slate-600 font-medium">Reply to Sender</span>
          </div> */}

          <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">

            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4 flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                </svg>
              </div>
              <span className="text-white text-sm font-semibold tracking-wide">Reply to Sender</span>
            </div>

            <div className="px-7 py-7">
              {submitted ? (
                <div className="flex flex-col items-center py-10 gap-4">
                  <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center">
                    <svg className="w-7 h-7 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div className="text-center">
                    <p className="text-gray-900 font-semibold text-lg">Message sent!</p>
                    <p className="text-gray-500 text-sm mt-1">
                      Your message has been delivered to{" "}
                      <span className="font-medium text-gray-700">{agentName || "the agent"}</span>.
                    </p>
                  </div>
                  <button
                    onClick={handleCancel}
                    className="mt-2 text-sm text-blue-600 hover:text-blue-800 font-medium transition-colors"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <>
                  <div className="flex flex-col items-center mb-6">
                    <div className="w-11 h-11 rounded-full bg-blue-50 flex items-center justify-center mb-3">
                      <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <h2 className="text-gray-900 font-bold text-lg">Send a message</h2>
                    <p className="text-gray-400 text-xs mt-1 text-center leading-relaxed max-w-xs">
                      Fill in the form below and hit{" "}
                      <span className="font-semibold text-gray-500">Send message</span>{" "}
                      to reply to this listing alert.
                    </p>
                  </div>

                  {/* TO field — name only, never email */}
                  <div className="mb-5">
                    <div className="flex items-start gap-2.5 border border-gray-200 rounded-xl px-3.5 py-2.5 bg-gray-50 min-h-[44px]">
                      <span className="text-[10px] font-bold text-gray-400 tracking-widest uppercase border border-gray-300 rounded-md px-2 py-0.5 bg-white flex-shrink-0 mt-0.5">
                        TO
                      </span>

                      {loading && (
                        <div className="flex flex-col gap-1.5 flex-1 pt-0.5">
                          <div className="h-3.5 w-32 bg-gray-200 rounded animate-pulse" />
                        </div>
                      )}

                      {!loading && fetchErr && (
                        <span className="text-sm text-red-400 italic">{fetchErr}</span>
                      )}

                      {!loading && !fetchErr && (
                        <div className="flex flex-col min-w-0">
                          {agentName ? (
                            <span className="text-sm text-blue-600 font-semibold leading-snug truncate pt-0.5">
                              {agentName}
                            </span>
                          ) : (
                            <span className="text-sm text-gray-300 italic">—</span>
                          )}
                        </div>
                      )}
                    </div>

                    {!campaignId && !agentId && (
                      <p className="mt-1.5 text-[11px] text-amber-500">
                        No <code>campaign_id</code> or <code>agent_id</code> param found — recipient cannot be resolved.
                      </p>
                    )}
                  </div>

                  <div className="mb-4">
                    <label className="block text-[11px] font-bold text-gray-500 uppercase tracking-widest mb-1.5">Your Name</label>
                    <input type="text" name="name" value={form.name} onChange={handleChange} placeholder="Full name"
                      className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm text-gray-800 placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white transition-all" />
                  </div>

                  <div className="mb-4">
                    <label className="block text-[11px] font-bold text-gray-500 uppercase tracking-widest mb-1.5">Email Address</label>
                    <input type="email" name="email" value={form.email} onChange={handleChange} placeholder="name@domain.com"
                      className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm text-gray-800 placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white transition-all" />
                  </div>

                  <div className="mb-4">
                    <label className="block text-[11px] font-bold text-gray-500 uppercase tracking-widest mb-1.5">Subject</label>
                    <div className="flex items-center border border-gray-200 rounded-xl bg-white focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent transition-all">
                      <span className="pl-3.5 pr-1 text-sm font-semibold text-gray-400 select-none flex-shrink-0">
                        RE:
                      </span>
                      <input
                        type="text"
                        name="subject"
                        value={form.subject}
                        onChange={handleChange}
                        placeholder="Enter subject"
                        className="w-full py-2.5 pr-3.5 text-sm text-gray-800 placeholder-gray-300 focus:outline-none bg-transparent"
                      />
                    </div>
                  </div>

                  <div className="mb-6">
                    <label className="block text-[11px] font-bold text-gray-500 uppercase tracking-widest mb-1.5">Your Message</label>
                    <textarea name="message" value={form.message} onChange={handleChange} placeholder="Write your message here..." rows={5}
                      className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm text-gray-800 placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white resize-none transition-all" />
                  </div>

                  {sendError && <p className="text-sm text-red-500 mb-3 -mt-2">{sendError}</p>}

                  <div className="flex justify-end gap-2.5">
                    <button type="button" onClick={handleCancel} disabled={sending}
                      className="px-5 py-2.5 text-sm font-semibold text-gray-500 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 hover:border-gray-300 transition-all disabled:opacity-40">
                      Cancel
                    </button>
                    <button type="button" onClick={handleSend} disabled={!isFormValid || sending}
                      className="flex items-center gap-2 px-5 py-2.5 text-sm font-bold text-white bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl hover:from-blue-700 hover:to-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed shadow-md hover:shadow-lg transition-all">
                      {sending ? "Sending..." : (
                        <>
                          <svg className="w-4 h-4 rotate-90" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                          </svg>
                          Send message
                        </>
                      )}
                    </button>
                  </div>
                </>
              )}
            </div>

            <div className="border-t border-gray-100 py-3.5 px-7 flex justify-center bg-primary">
              <p className="text-[11px] text-white/70">
                Powered by <span className="text-white font-bold">MyList</span><span className="text-[#fa3337] font-bold">IQ</span>
                <span className="text-white/40 mx-1.5">·</span>
                <span className="text-white font-semibold">Easy. Affordable.</span>{" "}
                <span className="text-[#fa3337] font-semibold">Smarter.</span>
              </p>
            </div>
          </div>
        </div>
      </main>

      <CallToActionFooter />

      <style>{`
        @keyframes marquee {
          from { transform: translateX(0); }
          to   { transform: translateX(-25%); }
        }
      `}</style>
    </div>
  );
}